import { Typography } from "@/components/common/Typography/Typography"
import { KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS } from "@/components/feature/KnowledgebaseFileListItem/partials/KnowledgebaseFileListLayout"
import { CreateKnowledgeBaseCategoryDialog } from "@/components/feature/KnowledgebaseFilters/CreateKnowledgeBaseCategoryDialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useGetUserConfig,
  useUpdateUserConfig,
} from "@/services/api/userConfigService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import type {
  KnowledgebaseFileTableUserConfig,
  UserConfigDocument,
} from "@/types/userConfig"
import { classNames } from "@/utils/classNames"
import { RefetchItemsContext } from "@/utils/context/refetchItemsStackedList"
import { getLocalizedLabel } from "@/utils/localizedLabel"
import { debounce } from "@/utils/optimisationUtils"
import { IconPlus, IconX } from "@tabler/icons-react"
import {
  columnResizingFeature,
  columnSizingFeature,
  createSortedRowModel,
  functionalUpdate,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type ColumnSizingState,
  type Header,
  type SortingState,
} from "@tanstack/react-table"
import { ChevronDown, ChevronUp } from "lucide-react"
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"
import { KnowledgebaseFileTableRow } from "./KnowledgebaseFileTableRow"

// TanStack Table 9 features are opt-in: this table sorts (which needs the
// sorted row model registered too) and sizes/resizes its columns.
export const knowledgebaseTableFeatures = tableFeatures({
  rowSortingFeature,
  columnSizingFeature,
  columnResizingFeature,
  sortedRowModel: createSortedRowModel(),
})
export type KnowledgebaseTableFeatures = typeof knowledgebaseTableFeatures

const EXPIRY_DATE_COLUMN_ID = "expiryDate"
const USER_CONFIG_SAVE_DELAY_MS = 500

export type CategoryColumnMeta = {
  type: "category"
  category: KnowledgeBaseCategory
}
type StaticColumnMeta = { type: "static" }
export type KnowledgebaseColumnMeta = CategoryColumnMeta | StaticColumnMeta
export type KnowledgebaseActiveCell = {
  fileId: string
  columnId: string
}
export type SetKnowledgebaseActiveCell = Dispatch<
  SetStateAction<KnowledgebaseActiveCell | null>
>

export const TABLE_BORDER = "border-grey3" as const

const COLUMN_SIZES = {
  name: 500,
  source: 130,
  date: 140,
  category: 240,
} as const

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const getPersistedFileTableConfig = (
  configuration: UserConfigDocument | undefined,
): KnowledgebaseFileTableUserConfig => {
  const knowledgebaseConfig = isRecord(configuration?.knowledgebase)
    ? configuration.knowledgebase
    : undefined
  const fileTableConfig = isRecord(knowledgebaseConfig?.fileTable)
    ? knowledgebaseConfig.fileTable
    : undefined

  const activeCategoryColumnIds = Array.isArray(
    fileTableConfig?.activeCategoryColumnIds,
  )
    ? fileTableConfig.activeCategoryColumnIds.filter(
        (columnId): columnId is string => typeof columnId === "string",
      )
    : []

  const isExpiryDateColumnActive =
    typeof fileTableConfig?.isExpiryDateColumnActive === "boolean"
      ? fileTableConfig.isExpiryDateColumnActive
      : false

  const columnWidthsById: Record<string, number> = isRecord(
    fileTableConfig?.columnWidthsById,
  )
    ? Object.entries(fileTableConfig.columnWidthsById).reduce<
        Record<string, number>
      >((accumulator, [columnId, width]) => {
        if (typeof width === "number" && Number.isFinite(width) && width > 0) {
          accumulator[columnId] = width
        }
        return accumulator
      }, {})
    : {}

  return {
    activeCategoryColumnIds,
    isExpiryDateColumnActive,
    columnWidthsById,
  }
}

// ─── Sort button ──────────────────────────────────────────────────────────────

interface SortButtonProps {
  header: Header<KnowledgebaseTableFeatures, KnowledgebaseFile, unknown>
  label: string
}

const SortButton: React.FC<SortButtonProps> = ({ header, label }) => {
  const sorted = header.column.getIsSorted()
  return (
    <button
      type="button"
      onClick={header.column.getToggleSortingHandler()}
      className={classNames(
        "group inline-flex items-center gap-1 text-grey5 transition-colors",
        "hover:text-grey6 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-yellow1",
        sorted ? "text-grey6" : "",
      )}
    >
      <Typography variant="textXS" as="span" className="font-medium">
        {label}
      </Typography>
      <span
        className={classNames(
          "transition-opacity",
          sorted ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
        aria-hidden="true"
      >
        {sorted === "asc" ? (
          <ChevronUp className="size-3.5" />
        ) : (
          <ChevronDown className="size-3.5" />
        )}
      </span>
    </button>
  )
}

// ─── Table ────────────────────────────────────────────────────────────────────

interface KnowledgebaseFileTableProps {
  files: KnowledgebaseFile[]
  availableCategories: KnowledgeBaseCategory[]
  selectedFileIds: Set<string>
  onToggleSelect: (id: string) => void
  isSelectionMode: boolean
  selectableCount?: number
  onSelectAll?: () => void
  onDeselectAll?: () => void
  refetchFiles: () => void
  showAllTags?: boolean
  activeFilterCategoryIds?: Set<string>
  onOpenTagDialog?: (file: KnowledgebaseFile) => void
}

export const KnowledgebaseFileTable: React.FC<KnowledgebaseFileTableProps> = ({
  files,
  availableCategories,
  selectedFileIds,
  onToggleSelect,
  isSelectionMode,
  selectableCount = 0,
  onSelectAll,
  onDeselectAll,
  refetchFiles,
  showAllTags = true,
  activeFilterCategoryIds,
  onOpenTagDialog,
}) => {
  const { translate, currentLanguage } = useTranslation()
  const { data: userConfigPayload, isPending: isUserConfigPending } =
    useGetUserConfig()
  const { mutate: updateUserConfig } = useUpdateUserConfig()
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ])
  const showTaggingFlow = useFeatureFlag("KB_TAGGING_FLOW")
  const [activeColumnIds, setActiveColumnIds] = useState<string[]>([])
  const [isExpiryDateColumnActive, setIsExpiryDateColumnActive] =
    useState(false)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false)
  const [activeCell, setActiveCell] = useState<KnowledgebaseActiveCell | null>(
    null,
  )
  const hasHydratedConfigRef = useRef(false)

  const configuration = userConfigPayload?.configuration
  const persistedFileTableConfig = useMemo(
    () => getPersistedFileTableConfig(configuration),
    [configuration],
  )

  const safeAvailableCategories = useMemo(
    () => (Array.isArray(availableCategories) ? availableCategories : []),
    [availableCategories],
  )

  const activeColumns = useMemo(
    () =>
      activeColumnIds
        .map((columnId) =>
          safeAvailableCategories.find((category) => category.id === columnId),
        )
        .filter((category): category is KnowledgeBaseCategory => !!category),
    [activeColumnIds, safeAvailableCategories],
  )

  const inactiveCategories = useMemo(
    () =>
      safeAvailableCategories.filter((c) => !activeColumnIds.includes(c.id)),
    [safeAvailableCategories, activeColumnIds],
  )

  useEffect(() => {
    if (hasHydratedConfigRef.current || isUserConfigPending) {
      return
    }

    setActiveColumnIds(persistedFileTableConfig.activeCategoryColumnIds)
    setIsExpiryDateColumnActive(
      persistedFileTableConfig.isExpiryDateColumnActive,
    )
    setColumnSizing(persistedFileTableConfig.columnWidthsById)
    hasHydratedConfigRef.current = true
  }, [isUserConfigPending, persistedFileTableConfig])

  const currentFileTableConfig = useMemo<KnowledgebaseFileTableUserConfig>(
    () => ({
      activeCategoryColumnIds: activeColumnIds,
      isExpiryDateColumnActive,
      columnWidthsById: columnSizing,
    }),
    [activeColumnIds, columnSizing, isExpiryDateColumnActive],
  )

  const persistTableConfig = useMemo(
    () =>
      debounce(
        (
          nextFileTableConfig: KnowledgebaseFileTableUserConfig,
          baseConfiguration: UserConfigDocument,
        ) => {
          updateUserConfig({
            configuration: {
              ...baseConfiguration,
              knowledgebase: {
                ...(isRecord(baseConfiguration.knowledgebase)
                  ? baseConfiguration.knowledgebase
                  : {}),
                fileTable: nextFileTableConfig,
              },
            },
          })
        },
        USER_CONFIG_SAVE_DELAY_MS,
      ),
    [updateUserConfig],
  )

  useEffect(() => {
    if (!hasHydratedConfigRef.current || isUserConfigPending) {
      return
    }

    const currentSerialized = JSON.stringify(currentFileTableConfig)
    const persistedSerialized = JSON.stringify(persistedFileTableConfig)

    if (currentSerialized === persistedSerialized) {
      return
    }

    persistTableConfig(currentFileTableConfig, configuration ?? {})
  }, [
    configuration,
    currentFileTableConfig,
    isUserConfigPending,
    persistTableConfig,
    persistedFileTableConfig,
  ])

  const columns = useMemo<
    ColumnDef<KnowledgebaseTableFeatures, KnowledgebaseFile>[]
  >(() => {
    const staticCols: ColumnDef<
      KnowledgebaseTableFeatures,
      KnowledgebaseFile
    >[] = [
      {
        id: "name",
        accessorFn: (row) => row.fileName,
        sortFn: (a, b) =>
          a.original.fileName.localeCompare(b.original.fileName, "de"),
        size: COLUMN_SIZES.name,
        minSize: 200,
        enableResizing: true,
        meta: { type: "static" } as KnowledgebaseColumnMeta,
      },
      {
        id: "source",
        accessorFn: (row) => row.source,
        size: COLUMN_SIZES.source,
        minSize: 80,
        enableResizing: true,
        meta: { type: "static" } as KnowledgebaseColumnMeta,
      },
      {
        id: "date",
        accessorFn: (row) => row.createdAt,
        sortFn: (a, b) =>
          new Date(a.original.createdAt).getTime() -
          new Date(b.original.createdAt).getTime(),
        size: COLUMN_SIZES.date,
        minSize: 80,
        enableResizing: true,
        meta: { type: "static" } as KnowledgebaseColumnMeta,
      },
    ]

    const expiryDateCol: ColumnDef<
      KnowledgebaseTableFeatures,
      KnowledgebaseFile
    >[] =
      isExpiryDateColumnActive && showTaggingFlow
        ? [
            {
              id: EXPIRY_DATE_COLUMN_ID,
              accessorFn: (row) => row.expiryDate ?? "",
              sortFn: (a, b) => {
                const aDate = a.original.expiryDate
                const bDate = b.original.expiryDate
                if (!aDate && !bDate) return 0
                if (!aDate) return 1
                if (!bDate) return -1
                return new Date(aDate).getTime() - new Date(bDate).getTime()
              },
              size: COLUMN_SIZES.date,
              minSize: 80,
              enableResizing: true,
              meta: { type: "static" } as KnowledgebaseColumnMeta,
            },
          ]
        : []

    const categoryCols: ColumnDef<
      KnowledgebaseTableFeatures,
      KnowledgebaseFile
    >[] = showTaggingFlow
      ? activeColumns.map((cat) => ({
          id: cat.id,
          // Sort by count of assigned tags in this category (more = first when asc)
          accessorFn: (row) =>
            cat.tags.filter((t) => row.tagIds?.includes(t.id)).length,
          size: COLUMN_SIZES.category,
          minSize: 100,
          enableResizing: true,
          meta: { type: "category", category: cat } as KnowledgebaseColumnMeta,
        }))
      : []

    return [...staticCols, ...expiryDateCol, ...categoryCols]
  }, [activeColumns, isExpiryDateColumnActive, showTaggingFlow])

  const table = useTable({
    features: knowledgebaseTableFeatures,
    data: files,
    columns,
    onColumnSizingChange: (updater) => {
      setColumnSizing((current) => functionalUpdate(updater, current))
    },
    onSortingChange: setSorting,
    columnResizeMode: "onChange",
    state: { sorting, columnSizing },
  })

  const [headerGroup] = table.getHeaderGroups()
  const totalSize = table.getTotalSize()

  return (
    <div
      role="table"
      className={`max-h-full min-h-0 w-full overflow-auto rounded border ${TABLE_BORDER}`}
    >
      {/* Header */}
      <div
        role="row"
        style={{ minWidth: totalSize }}
        className={`sticky top-0 z-10 flex items-stretch border-b ${TABLE_BORDER} bg-grey1 text-grey5`}
      >
        {headerGroup.headers.map((header) => {
          const meta = header.column.columnDef.meta as
            | KnowledgebaseColumnMeta
            | undefined
          const categoryMeta = meta?.type === "category" ? meta : null

          return (
            <div
              key={header.id}
              role="columnheader"
              style={{
                width: header.getSize(),
                flexShrink: 0,
                position: "relative",
              }}
              className={`flex items-center overflow-hidden border-r ${TABLE_BORDER} px-3 py-2.5`}
            >
              {header.column.id === "name" && (
                <div
                  className={classNames(
                    KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS,
                    "w-full",
                  )}
                >
                  <div className="flex h-6 w-6 items-center justify-center">
                    {isSelectionMode && (
                      <Checkbox
                        checked={
                          selectableCount > 0 &&
                          selectedFileIds.size === selectableCount
                        }
                        onCheckedChange={(checked) =>
                          checked ? onSelectAll?.() : onDeselectAll?.()
                        }
                        aria-label={translate("KNOWLEDGEBASE/BULK_SELECT_ALL")}
                        className="h-5 w-5"
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <SortButton
                      header={header}
                      label={translate("KNOWLEDGEBASE/SORT_NAME")}
                    />
                  </div>
                </div>
              )}

              {header.column.id === "source" && (
                <SortButton
                  header={header}
                  label={translate("KNOWLEDGEBASE/FILTER_SOURCE")}
                />
              )}

              {header.column.id === "date" && (
                <SortButton
                  header={header}
                  label={translate("KNOWLEDGEBASE/SORT_DATE")}
                />
              )}

              {header.column.id === EXPIRY_DATE_COLUMN_ID && (
                <div className="flex min-w-0 flex-1 items-center gap-1 pr-4">
                  <SortButton
                    header={header}
                    label={translate("KNOWLEDGEBASE/EXPIRY_DATE_LABEL")}
                  />
                  <button
                    type="button"
                    onClick={() => setIsExpiryDateColumnActive(false)}
                    className="ml-auto shrink-0 rounded p-0.5 hover:bg-grey4 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-yellow1"
                    aria-label={translate("KNOWLEDGEBASE/REMOVE_COLUMN")}
                  >
                    <IconX size={12} />
                  </button>
                </div>
              )}

              {categoryMeta && (
                <div className="flex min-w-0 flex-1 items-center gap-1 pr-4">
                  <SortButton
                    header={header}
                    label={getLocalizedLabel(
                      categoryMeta.category.displayNameEn,
                      categoryMeta.category.displayNameDe,
                      currentLanguage,
                    )}
                  />
                  {activeFilterCategoryIds?.has(categoryMeta.category.id) && (
                    <span
                      className="ml-0.5 inline-block size-1.5 shrink-0 rounded-full bg-yellow1"
                      aria-label={translate("KNOWLEDGEBASE/FILTER_ACTIVE_ARIA")}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setActiveColumnIds((prev) =>
                        prev.filter(
                          (columnId) => columnId !== categoryMeta.category.id,
                        ),
                      )
                    }
                    className="ml-auto shrink-0 rounded p-0.5 hover:bg-grey4 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-yellow1"
                    aria-label={`${translate("KNOWLEDGEBASE/REMOVE_COLUMN")} ${getLocalizedLabel(
                      categoryMeta.category.displayNameEn,
                      categoryMeta.category.displayNameDe,
                      currentLanguage,
                    )}`}
                  >
                    <IconX size={12} />
                  </button>
                </div>
              )}

              {/* Resize handle */}
              {header.column.getCanResize() && (
                <div
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  className={classNames(
                    "absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize touch-none select-none",
                    "bg-transparent hover:bg-yellow1",
                    header.column.getIsResizing() ? "bg-yellow1" : "",
                  )}
                />
              )}
            </div>
          )
        })}

        {/* Add Column */}
        {showTaggingFlow &&
          (inactiveCategories.length > 0 || !isExpiryDateColumnActive) && (
            <div className={`flex items-center ${TABLE_BORDER} px-3`}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label={translate("KNOWLEDGEBASE/ADD_COLUMN")}
                  >
                    <IconPlus size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {!isExpiryDateColumnActive && (
                    <DropdownMenuItem
                      onClick={() => setIsExpiryDateColumnActive(true)}
                    >
                      {/* <IconCalendar size={14} /> */}
                      {translate("KNOWLEDGEBASE/EXPIRY_DATE_LABEL")}
                    </DropdownMenuItem>
                  )}
                  {inactiveCategories.map((category) => (
                    <DropdownMenuItem
                      key={category.id}
                      onClick={() =>
                        setActiveColumnIds((prev) =>
                          prev.includes(category.id)
                            ? prev
                            : [...prev, category.id],
                        )
                      }
                    >
                      {getLocalizedLabel(
                        category.displayNameEn,
                        category.displayNameDe,
                        currentLanguage,
                      )}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setIsCreateCategoryOpen(true)}
                  >
                    <IconPlus size={14} />
                    {translate("KNOWLEDGEBASE/CATEGORY_CREATE_TRIGGER")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
      </div>

      {/* Rows */}
      <RefetchItemsContext.Provider value={refetchFiles}>
        <div role="rowgroup" style={{ minWidth: totalSize }}>
          {table.getRowModel().rows.map((row, index) => (
            <KnowledgebaseFileTableRow
              key={row.id}
              row={row}
              isLast={index === table.getRowModel().rows.length - 1}
              selectedFileIds={selectedFileIds}
              onToggleSelect={onToggleSelect}
              isSelectionMode={isSelectionMode}
              availableCategories={safeAvailableCategories}
              activeColumns={activeColumns}
              activeCell={activeCell}
              onActiveCellChange={setActiveCell}
              showAllTags={showAllTags}
              onOpenTagDialog={onOpenTagDialog}
            />
          ))}
        </div>
      </RefetchItemsContext.Provider>

      <CreateKnowledgeBaseCategoryDialog
        open={isCreateCategoryOpen}
        onOpenChange={setIsCreateCategoryOpen}
      />
    </div>
  )
}
